import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldCheck, Search, Clock, XCircle, Ban, HelpCircle, CheckCircle2, Eye, ExternalLink, Trash2, Pencil } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";
import { Label } from "@/components/ui/label";
import LocationsEditor from "@/components/LocationsEditor";

const PROFESSION_LABELS = {
  medico_medicina_generale: "Medico di Medicina Generale",
  medico_specialista: "Medico Specialista",
  laboratorio: "Laboratorio",
  diagnostica: "Struttura Diagnostica",
  altro_professionista: "Altro Professionista",
};

const STATUS_META = {
  pending:   { label: "In verifica", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  verified:  { label: "Verificato",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected:  { label: "Rifiutato",   cls: "bg-red-50 text-red-800 border-red-200" },
  suspended: { label: "Sospeso",     cls: "bg-red-50 text-red-800 border-red-200" },
  expired:   { label: "Scaduto",     cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function AdminVerificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("status") || "pending";
  const [tab, setTab] = useState(initialTab);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actionMode, setActionMode] = useState(null); // 'approve'|'reject'|'suspend'|'info'
  const [fnomceoMode, setFnomceoMode] = useState(false);
  const [fnomceoResult, setFnomceoResult] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      company_name: u.company_name || "",
      specialization: u.specialization || "",
      academic_title: u.academic_title || "",
      phone: u.phone || "",
      email: u.email || "",
      website: u.website || "",
      bio: u.bio || "",
      order_province: u.order_province || "",
      order_number: u.order_number || "",
      locations: u.locations || [],
    });
  };

  const onEditSave = async () => {
    if (!editTarget || !editForm) return;
    setEditSaving(true);
    try {
      const clean = { ...editForm };
      clean.locations = (clean.locations || [])
        .map((l) => ({ id: l.id, name: (l.name || "").trim(), city: (l.city || "").trim(),
                       address: (l.address || "").trim(), phone: (l.phone || "").trim(),
                       email: (l.email || "").trim() }))
        .filter((l) => l.name && l.city);
      // Rimuovi email se invariata per non fare check di unicità inutile
      if (clean.email && clean.email.toLowerCase() === (editTarget.email || "").toLowerCase()) {
        delete clean.email;
      }
      await api.patch(`/admin/users/${editTarget.id}`, clean);
      toast.success("Scheda anagrafica aggiornata");
      setEditTarget(null); setEditForm(null);
      load(tab);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setEditSaving(false); }
  };

  const onAdminDelete = async () => {
    if (!deleteTarget || deleteConfirm.trim().toUpperCase() !== "ELIMINA") return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success("Account eliminato");
      setDeleteTarget(null); setDeleteConfirm("");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setDeleting(false); }
  };

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/verifications", { params: { status } });
      setItems(data);
      // sync URL param so navigation from stats deep-links correctly
      if (status !== searchParams.get("status")) {
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          if (status === "pending") p.delete("status"); else p.set("status", status);
          return p;
        }, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const filtered = items.filter((u) => {
    if (!q) return true;
    const s = `${u.first_name || ""} ${u.last_name || ""} ${u.company_name || ""} ${u.email} ${u.fiscal_code} ${u.order_number}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const displayName = (u) => (u?.company_name && (u?.profession === "laboratorio" || u?.profession === "diagnostica"))
    ? u.company_name
    : `${u?.first_name || ""} ${u?.last_name || ""}`.trim() || u?.email;

  const doAction = async () => {
    if (!selected || !actionMode) return;
    setSaving(true);
    const endpoint = {
      approve: "approve", reject: "reject", suspend: "suspend", info: "request-info",
    }[actionMode];
    try {
      await api.post(`/admin/verifications/${selected.id}/${endpoint}`, { note });
      toast.success("Operazione registrata");
      setActionMode(null); setNote(""); setSelected(null);
      load(tab);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#10B981] text-white flex items-center justify-center shadow-lg shadow-emerald-500/20">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#1E3A8A]/80">Amministrazione</p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">Verifica professionisti</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Controlla e attiva i professionisti iscritti. Ogni azione è registrata nell'audit log.
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 items-stretch mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email, CF, iscrizione…"
                 className="pl-9 h-11 bg-white border-slate-200 text-white placeholder:text-slate-500 focus:border-emerald-400/40" data-testid="admin-verif-search" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 md:grid-cols-6 mb-4 h-auto bg-slate-50 border border-slate-200">
          <TabsTrigger value="pending" data-testid="tab-pending" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1E3A8A] data-[state=active]:to-[#10B981] data-[state=active]:text-white text-slate-300">In verifica</TabsTrigger>
          <TabsTrigger value="verified" data-testid="tab-verified" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1E3A8A] data-[state=active]:to-[#10B981] data-[state=active]:text-white text-slate-300">Verificati</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1E3A8A] data-[state=active]:to-[#10B981] data-[state=active]:text-white text-slate-300">Rifiutati</TabsTrigger>
          <TabsTrigger value="suspended" data-testid="tab-suspended" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1E3A8A] data-[state=active]:to-[#10B981] data-[state=active]:text-white text-slate-300">Sospesi</TabsTrigger>
          <TabsTrigger value="expired" data-testid="tab-expired" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1E3A8A] data-[state=active]:to-[#10B981] data-[state=active]:text-white text-slate-300">Scaduti</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1E3A8A] data-[state=active]:to-[#10B981] data-[state=active]:text-white text-slate-300">Tutti</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          {loading && <div className="p-6 flex justify-center"><SynapseLoader label="Caricamento verifiche…" /></div>}
          {!loading && filtered.length === 0 && (
            <Card className="p-8 text-center text-slate-500 bg-white border-slate-200 rounded-2xl">
              Nessun professionista in questo stato.
            </Card>
          )}
          <div className="space-y-3">
            {filtered.map((u) => (
              <Card key={u.id} data-testid={`verif-row-${u.id}`}
                    className="p-4 md:p-5 bg-white border-slate-200 rounded-2xl shadow-sm hover:border-emerald-400/30 hover:bg-white transition-all">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">
                        {displayName(u)}
                      </span>
                      {(u.profession === "laboratorio" || u.profession === "diagnostica") && u.company_name && (u.first_name || u.last_name) && (
                        <span className="text-xs text-slate-500">
                          — referente {u.first_name} {u.last_name}
                        </span>
                      )}
                      <StatusBadge status={u.verification_status} />
                      {u.is_admin && <Badge variant="secondary" className="text-[10px] bg-white/10 text-slate-200 border-slate-200">ADMIN</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {PROFESSION_LABELS[u.profession] || u.profession}
                      {u.specialization && <> · <span className="text-slate-200">{u.specialization}</span></>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">{u.email} · {u.phone || "—"}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      CF: <span className="font-mono">{u.fiscal_code || "—"}</span>{" · "}
                      Ordine: <span className="font-semibold text-slate-200">{u.order_province || "—"} N° {u.order_number || "—"}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 md:justify-end">
                    <Button variant="outline" size="sm" onClick={() => setSelected(u)}
                            data-testid={`verif-detail-${u.id}`}
                            className="border-slate-200 bg-slate-50 text-slate-200 hover:bg-white/[0.10] hover:text-white">
                      <Eye className="h-4 w-4 mr-1" /> Dettagli
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(u)}
                            data-testid={`verif-edit-${u.id}`}
                            className="border-[#1E3A8A]/30 bg-[#1E3A8A]/5 text-[#1E3A8A] hover:bg-[#1E3A8A]/10">
                      <Pencil className="h-4 w-4 mr-1" /> Anagrafica
                    </Button>
                    {!u.is_admin && (
                      <Button variant="outline" size="sm"
                              onClick={() => { setSelected(u); setFnomceoMode(true); }}
                              data-testid={`verif-fnomceo-${u.id}`}
                              className="border-emerald-400/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20">
                        <ShieldCheck className="h-4 w-4 mr-1" /> FNOMCeO
                      </Button>
                    )}
                    {u.verification_status !== "verified" && (
                      <Button size="sm" onClick={() => { setSelected(u); setActionMode("approve"); }}
                              data-testid={`verif-approve-${u.id}`}
                              className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-md shadow-emerald-500/20">
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approva
                      </Button>
                    )}
                    {u.verification_status !== "rejected" && !u.is_admin && (
                      <Button size="sm" variant="outline" onClick={() => { setSelected(u); setActionMode("reject"); }}
                              data-testid={`verif-reject-${u.id}`}
                              className="border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20">
                        <XCircle className="h-4 w-4 mr-1" /> Rifiuta
                      </Button>
                    )}
                    {!u.is_admin && u.verification_status !== "suspended" && (
                      <Button size="sm" variant="outline" onClick={() => { setSelected(u); setActionMode("suspend"); }}
                              data-testid={`verif-suspend-${u.id}`}
                              className="border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20">
                        <Ban className="h-4 w-4 mr-1" /> Sospendi
                      </Button>
                    )}
                    {!u.is_admin && (
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); setDeleteConfirm(""); }}
                              data-testid={`verif-delete-${u.id}`}
                              className="text-red-300 hover:bg-red-500/10 hover:text-red-200">
                        <Trash2 className="h-4 w-4 mr-1" /> Elimina
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => { setSelected(u); setActionMode("info"); }}
                            data-testid={`verif-info-${u.id}`}
                            className="text-slate-300 hover:bg-slate-50 hover:text-white">
                      <HelpCircle className="h-4 w-4 mr-1" /> Info
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Details dialog */}
      <Dialog open={!!selected && !actionMode} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="verif-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {selected ? displayName(selected) : ""}
            </DialogTitle>
            <DialogDescription>Dettaglio richiesta di verifica</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Detail label="Stato"><StatusBadge status={selected.verification_status} /></Detail>
                {selected.company_name && (selected.profession === "laboratorio" || selected.profession === "diagnostica") && (
                  <Detail label="Ragione Sociale">{selected.company_name}</Detail>
                )}
                {(selected.first_name || selected.last_name) && (
                  <Detail label={(selected.profession === "laboratorio" || selected.profession === "diagnostica") ? "Referente" : "Nome"}>
                    {selected.first_name} {selected.last_name}
                  </Detail>
                )}
                <Detail label="Email">{selected.email}</Detail>
                <Detail label="Telefono">{selected.phone || "—"}</Detail>
                <Detail label="Codice fiscale"><span className="font-mono">{selected.fiscal_code}</span></Detail>
                <Detail label="Data di nascita">{selected.birth_date || "—"}</Detail>
                <Detail label="Professione">{PROFESSION_LABELS[selected.profession] || selected.profession}</Detail>
                <Detail label="Specializzazione">{selected.specialization || "—"}</Detail>
                <Detail label="Ordine">{selected.order_province} · N° {selected.order_number}</Detail>
              </div>
              <VerificationTimeline userId={selected.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirm(""); } }}>
        <DialogContent data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Elimina account
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare definitivamente <strong>{deleteTarget?.company_name || `${deleteTarget?.first_name || ""} ${deleteTarget?.last_name || ""}`.trim() || deleteTarget?.email}</strong>. I dati personali verranno cancellati; le conversazioni cliniche restano tracciate come "Utente cancellato" (GDPR art. 17). Digita <strong>ELIMINA</strong> per confermare.
            </DialogDescription>
          </DialogHeader>
          <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
                 data-testid="admin-delete-confirm" placeholder="ELIMINA" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }}>Annulla</Button>
            <Button disabled={deleting || deleteConfirm.trim().toUpperCase() !== "ELIMINA"}
                    onClick={onAdminDelete}
                    data-testid="admin-delete-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FNOMCeO assisted verification */}
      <Dialog open={fnomceoMode} onOpenChange={(o) => { if (!o) { setFnomceoMode(false); setFnomceoResult(null); } }}>
        <DialogContent data-testid="fnomceo-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#1E3A8A]" />
              Verifica su FNOMCeO
            </DialogTitle>
            <DialogDescription>
              {selected?.first_name} {selected?.last_name} · Ordine {selected?.order_province} N° {selected?.order_number}
            </DialogDescription>
          </DialogHeader>
          {!fnomceoResult && (
            <div className="text-sm text-slate-600 space-y-3">
              <p>
                Synappsi non fa una verifica automatica sull'anagrafica FNOMCeO
                (il portale <span className="font-mono">albounico.fnomceo.it</span> è protetto da anti-bot
                e non espone API pubbliche). Questa è una <strong>verifica assistita</strong>: apri
                il portale ufficiale, cerca i dati e confermali.
              </p>
              <Button onClick={async () => {
                setSaving(true);
                try {
                  const { data } = await api.post(`/admin/verifications/${selected.id}/fnomceo-check`);
                  setFnomceoResult(data);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
                } finally { setSaving(false); }
              }} disabled={saving} data-testid="fnomceo-run"
                      className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
                {saving ? "Verifica…" : "Esegui verifica assistita"}
              </Button>
            </div>
          )}
          {fnomceoResult && (
            <div className="space-y-3 text-sm">
              <div className={`p-3 rounded-lg border ${
                fnomceoResult.portal_reachable ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                               : "bg-orange-50 border-orange-200 text-orange-800"
              }`}>
                <div className="font-semibold">
                  {fnomceoResult.portal_reachable ? "Portale FNOMCeO raggiungibile" : "Verifica manuale richiesta"}
                </div>
                <div className="text-xs mt-1">{fnomceoResult.message}</div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 space-y-1 text-xs">
                <div className="font-semibold text-slate-700 text-[11px] uppercase tracking-wider">Dati da cercare</div>
                <div>Cognome: <span className="font-semibold">{fnomceoResult.hint?.search_last_name || "—"}</span></div>
                <div>Nome: <span className="font-semibold">{fnomceoResult.hint?.search_first_name || "—"}</span></div>
                <div>Provincia: <span className="font-semibold">{fnomceoResult.hint?.search_province || "—"}</span></div>
                <div>N° iscrizione: <span className="font-semibold">{fnomceoResult.hint?.search_number || "—"}</span></div>
              </div>
              <a href={fnomceoResult.manual_url} target="_blank" rel="noopener noreferrer"
                 data-testid="fnomceo-open-portal"
                 className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] hover:underline">
                <ExternalLink className="h-4 w-4" /> Apri albounico.fnomceo.it
              </a>
              <div className="text-[11px] text-slate-500 italic border-t border-slate-100 pt-2">
                Il risultato è stato salvato nello storico verifica dell'utente e nell'audit log.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFnomceoMode(false); setFnomceoResult(null); }}
                    data-testid="fnomceo-close">Chiudi</Button>
            {fnomceoResult && selected?.verification_status !== "verified" && (
              <Button onClick={() => { setFnomceoMode(false); setFnomceoResult(null); setActionMode("approve"); }}
                      data-testid="fnomceo-approve-after"
                      className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approva ora
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit anagrafica dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) { setEditTarget(null); setEditForm(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Pencil className="h-5 w-5 text-[#1E3A8A]" /> Modifica scheda anagrafica
            </DialogTitle>
            <DialogDescription>
              Aggiorna i dati che compaiono nella rubrica: nome/ragione sociale, telefono, email, sito web, indirizzo, sedi.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(editTarget?.profession === "laboratorio" || editTarget?.profession === "diagnostica") && (
                  <div className="md:col-span-2">
                    <Label>Ragione Sociale</Label>
                    <Input value={editForm.company_name}
                           onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                           data-testid="admin-edit-company" />
                  </div>
                )}
                <div>
                  <Label>Nome</Label>
                  <Input value={editForm.first_name}
                         onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                         data-testid="admin-edit-first-name" />
                </div>
                <div>
                  <Label>Cognome</Label>
                  <Input value={editForm.last_name}
                         onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                         data-testid="admin-edit-last-name" />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input value={editForm.phone}
                         onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                         placeholder="+39 …" data-testid="admin-edit-phone" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={editForm.email}
                         onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                         data-testid="admin-edit-email" />
                </div>
                <div className="md:col-span-2">
                  <Label>Sito web</Label>
                  <Input value={editForm.website}
                         onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                         placeholder="Es. https://www.studio.it"
                         data-testid="admin-edit-website" />
                </div>
                <div>
                  <Label>Specializzazione / Ambito</Label>
                  <Input value={editForm.specialization}
                         onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
                         data-testid="admin-edit-specialization" />
                </div>
                <div>
                  <Label>Titolo accademico</Label>
                  <Input value={editForm.academic_title}
                         onChange={(e) => setEditForm({ ...editForm, academic_title: e.target.value })}
                         data-testid="admin-edit-academic-title" />
                </div>
                <div>
                  <Label>Ordine / Provincia</Label>
                  <Input value={editForm.order_province}
                         onChange={(e) => setEditForm({ ...editForm, order_province: e.target.value })}
                         data-testid="admin-edit-order-province" />
                </div>
                <div>
                  <Label>N° iscrizione</Label>
                  <Input value={editForm.order_number}
                         onChange={(e) => setEditForm({ ...editForm, order_number: e.target.value })}
                         data-testid="admin-edit-order-number" />
                </div>
                <div className="md:col-span-2">
                  <Label>Descrizione</Label>
                  <Textarea rows={3} value={editForm.bio}
                            onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                            maxLength={800}
                            data-testid="admin-edit-bio" />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Sedi / Indirizzi
                </div>
                <LocationsEditor value={editForm.locations}
                                 onChange={(locs) => setEditForm({ ...editForm, locations: locs })}
                                 testidPrefix="admin-edit-loc" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditTarget(null); setEditForm(null); }}
                    data-testid="admin-edit-cancel">Annulla</Button>
            <Button onClick={onEditSave} disabled={editSaving}
                    data-testid="admin-edit-save"
                    className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              {editSaving ? "Salvataggio…" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action dialog */}
      <Dialog open={!!actionMode} onOpenChange={(o) => !o && (setActionMode(null), setNote(""))}>
        <DialogContent data-testid="verif-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {actionMode === "approve" && "Approvare il professionista?"}
              {actionMode === "reject" && "Rifiutare la verifica?"}
              {actionMode === "suspend" && "Sospendere l'account?"}
              {actionMode === "info" && "Richiedere informazioni aggiuntive"}
            </DialogTitle>
            <DialogDescription>
              {selected?.first_name} {selected?.last_name} · {selected?.email}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-semibold text-slate-600">Nota (opzionale)</label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Motivo o istruzioni per l'utente…"
                      data-testid="verif-action-note" />
            <p className="text-[11px] text-slate-500 mt-1">
              La nota verrà registrata nello storico verifica e nel log.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionMode(null); setNote(""); }}
                    data-testid="verif-action-cancel">Annulla</Button>
            <Button onClick={doAction} disabled={saving}
                    data-testid="verif-action-confirm"
                    className={
                      actionMode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" :
                      actionMode === "info" ? "bg-[#1E3A8A] hover:bg-[#1E40AF]" :
                      "bg-red-600 hover:bg-red-700"
                    }>
              {saving ? "Salvataggio…" : "Conferma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{children}</div>
    </div>
  );
}

function VerificationTimeline({ userId }) {
  const [entries, setEntries] = React.useState([]);
  React.useEffect(() => {
    api.get(`/admin/verifications/${userId}`).then((r) => setEntries(r.data.verification_history || []))
      .catch(() => {});
  }, [userId]);
  if (!entries.length) return null;
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
        Storico verifica
      </div>
      <div className="space-y-2 border-l-2 border-slate-200 pl-4">
        {entries.slice().reverse().map((e) => (
          <div key={e.id} className="relative">
            <div className="absolute -left-[21px] top-1.5 h-3 w-3 rounded-full bg-white border-2 border-[#1E3A8A]" />
            <div className="text-xs">
              <span className="font-semibold text-slate-900">{ACTION_LABEL[e.action] || e.action}</span>
              <span className="text-slate-500"> · {new Date(e.at).toLocaleString("it-IT")}</span>
            </div>
            <div className="text-xs text-slate-500">di {e.by_user_name || "sistema"}</div>
            {e.note && <div className="text-xs text-slate-600 italic mt-0.5">"{e.note}"</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
const ACTION_LABEL = {
  submitted: "Richiesta sottomessa",
  approved: "Verifica approvata",
  rejected: "Verifica rifiutata",
  suspended: "Sospeso",
  info_requested: "Info richieste",
  seeded: "Creato dal sistema",
};
